import React, { useCallback, useEffect } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

export type SeekbarProps = {
  enabled: boolean;
  hls: Hls;
  code: string;
  duration: number;
  videoContainer: React.RefObject<HTMLDivElement>;
};

const getTimeInBrTiming = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time - (hours * 3600)) / 60);
  const seconds = Math.floor(time - (hours * 3600) - (minutes * 60));

  if (time > 3600) {
    return hours.toString().padStart(2, '0') + ':' +
           minutes.toString().padStart(2, '0') + ':' +
           seconds.toString().padStart(2, '0').substring(0, 2);
  } else {
    return minutes.toString().padStart(2, '0') + ':' +
           seconds.toString().padStart(2, '0').substring(0, 2);
  }
};

const Seekbar = ({ enabled, hls, code, duration, videoContainer }: SeekbarProps) => {

  const onTimeUpdate = useCallback(() => {
    console.log('onTimeUpdate');

    if (!hls || !hls.media || !videoContainer || !videoContainer?.current) {
      return;
    }

    const currentTime = hls.media.currentTime;
    const totalTime = hls.media.duration;
    const progressPercentage = (currentTime / totalTime) * 100;
    const currentTimeBr = getTimeInBrTiming(currentTime);

    const progressBar = videoContainer?.current.querySelector('.smartplayer__seek') as HTMLInputElement;
    progressBar?.setAttribute('style', `--value: ${progressPercentage.toString()}%;`);
    progressBar.setAttribute('aria-valuenow', (currentTime).toString());
    progressBar.setAttribute('data-time', currentTimeBr);
    progressBar.value = (progressPercentage).toFixed(2).toString();

    const currentTimeComponent = videoContainer?.current.querySelector('.smartplayer__seekBarCurrentTime') as HTMLInputElement
    currentTimeComponent.innerHTML = getTimeInBrTiming(currentTime);
    currentTimeComponent.style.left = `${progressPercentage}%`;

    if (currentTime > 0) {
      localStorage.setItem(code + '--lastTime', (currentTime).toString());
    }
  }, [hls, hls?.media, videoContainer]);

  const onInputChange = useCallback((event: any) => {
    console.log('onInputChange');

    if (!hls || !hls.media || !videoContainer || !videoContainer?.current) {
      return;
    }

    const progressPercentage = parseFloat(event.target.value);
    const newTime = (progressPercentage / 100) * hls.media.duration;
    hls.media.currentTime = newTime;

    const progressBar = videoContainer?.current?.querySelector('.smartplayer__seek') as HTMLInputElement;
    progressBar?.setAttribute('style', `--value: ${progressPercentage.toString()}%;`);
    progressBar?.setAttribute('aria-valuenow', newTime.toString());
    progressBar.value = (progressPercentage).toFixed(2).toString();

    if (progressBar.value === '100') {
      videoContainer?.current?.classList.add('smartplayer--ended');
    } else {
      videoContainer?.current?.classList.remove('smartplayer--ended');
    }
  }, [hls, hls?.media, videoContainer]);

  const onProgress = useCallback(() => {
    console.log('onProgress');

    if (!hls || !hls.media || !videoContainer || !videoContainer?.current) {
      return;
    }

    const buffered = hls.media.buffered;
    const duration = hls.media.duration;
    let bufferedEnd = 0;

    for (let i = 0; i < buffered.length; i++) {
      bufferedEnd = Math.max(bufferedEnd, buffered.end(i));
    }

    const progressPercentage = (bufferedEnd / duration) * 100;

    console.log('progressPercentage', progressPercentage);

    if (videoContainer.current && videoContainer.current.querySelector('.smartplayer__buffer')) {
      const progressElement = videoContainer.current.querySelector('.smartplayer__buffer') as HTMLProgressElement;
      progressElement.value = parseFloat(progressPercentage.toFixed(2).toString());
    }
  }, [hls, hls?.media, videoContainer]);

  const getTime = useCallback(() => {
    return getTimeInBrTiming(duration);
  }, [duration]);

  useEffect(() => {
    if (!hls || !hls.media) {
      return;
    }

    hls.media.addEventListener('timeupdate', onTimeUpdate);

    hls.media.addEventListener('progress', onProgress);

    videoContainer?.current?.querySelector('.smartplayer__seek')?.addEventListener('input', onInputChange);

    return () => {
      hls?.media?.removeEventListener('timeupdate', onTimeUpdate);
      hls?.media?.removeEventListener('progress', onProgress);
      videoContainer?.current?.querySelector('.smartplayer__seek')?.removeEventListener('input', onInputChange);
    };
  }, [hls, hls?.media, videoContainer])

  return (
    <div className="smartplayer__seekBarContainer">
      {
        enabled ?
        (<div className="smartplayer__seekBar">
          <input className="smartplayer__seek" type="range" defaultValue="0" min="0" max="100" step="0.01" autoComplete="off" role="slider" aria-label="Progresso" aria-valuemin={0} aria-valuemax={100} style={{ "--value": '0%' } as React.CSSProperties} />
          <div className="smartplayer__seekBarCurrentTime">00:00</div>
          <progress className="smartplayer__buffer" defaultValue="0" max="100" role="progressbar" aria-hidden="true">% carregado</progress>
          <div className="smartplayer__bg"></div>
          <div className="smartplayer__seekBarDuration">
            {getTime()}
          </div>
        </div>) : null}
    </div>
  );
};

export default Seekbar;
